/**
 * Riga di una partita, uguale nell'elenco e nel giorno scelto del calendario
 * (V16): il foglietto con la data, con la fascia del colore dello stato o, a
 * partita conclusa, dell'esito,
 * avversario e stato, poi ora, casa o trasferta, competizione e luogo; dal
 * fischio d'inizio il risultato, prima la squadra di casa, con l'esito a
 * partita conclusa.
 */

import { Link } from 'react-router-dom';
import { BusIcon, ClockIcon, HouseIcon, MapPinIcon, TrophyIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  formatoGiornoSettimana,
  formatoMeseBreve,
  formatoNumeroGiorno,
  formatoOra,
} from '../allenamenti/allenamenti.js';
import {
  COLORE_ESITO,
  COLORE_SEGNO,
  ETICHETTA_ESITO,
  ETICHETTA_SEGNO,
  LETTERA_ESITO,
  type PartitaInElenco,
  type Segno,
  esitoDi,
} from './raggruppa.js';
import { punteggio } from './partita.js';

export function RigaPartita({
  partita: p,
  segno,
  ss,
}: {
  partita: PartitaInElenco;
  segno: Segno;
  ss: string;
}) {
  const data = new Date(p.dataOra);
  const annullata = p.stato === 'annullata';
  const risultato = p.stato === 'in_corso' || p.stato === 'conclusa';
  const esito = p.stato === 'conclusa' ? esitoDi(p) : null;
  const Campo = p.inCasa ? HouseIcon : BusIcon;
  return (
    <Link
      to={`/sq/${ss}/partite/${p.id}`}
      className="flex items-center gap-3 rounded-lg border border-border bg-card p-2.5"
    >
      <span
        className={cn(
          'flex w-14 shrink-0 flex-col overflow-hidden rounded-md border border-border bg-background text-center',
          annullata && 'opacity-60',
        )}
      >
        {/* Il colore dello stato; a partita conclusa, quello dell'esito. */}
        <span
          className={cn(
            'py-0.5 text-[11px] font-semibold uppercase',
            esito ? COLORE_ESITO[esito] : COLORE_SEGNO[segno],
          )}
        >
          {formatoGiornoSettimana.format(data).replace('.', '')}
        </span>
        <span className="text-xl leading-7 font-bold tabular-nums">
          {formatoNumeroGiorno.format(data)}
        </span>
        <span className="pb-0.5 text-[11px] text-muted-foreground uppercase">
          {formatoMeseBreve.format(data).replace('.', '')}
        </span>
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className={cn(
              'min-w-0 truncate text-lg font-semibold',
              annullata && 'text-muted-foreground line-through',
            )}
          >
            {p.avversario}
          </span>
          <Badge className={COLORE_SEGNO[segno]}>{ETICHETTA_SEGNO[segno]}</Badge>
        </span>
        <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5 tabular-nums">
            <ClockIcon aria-hidden className="size-4 shrink-0" />
            {formatoOra.format(data)}
          </span>
          <span className="flex items-center gap-1.5">
            <Campo aria-hidden className="size-4 shrink-0" />
            {p.inCasa ? 'Casa' : 'Trasferta'}
          </span>
          <span className="flex min-w-0 items-center gap-1.5">
            <TrophyIcon aria-hidden className="size-4 shrink-0" />
            <span className="truncate">{p.competizione}</span>
          </span>
          {p.luogo && (
            <span className="flex min-w-0 items-center gap-1.5">
              <MapPinIcon aria-hidden className="size-4 shrink-0" />
              <span className="truncate">{p.luogo}</span>
            </span>
          )}
        </span>
      </span>

      {risultato && (
        <span className="flex shrink-0 flex-col items-end gap-1">
          <span className="text-xl font-bold whitespace-nowrap tabular-nums">
            {punteggio(p, p.inCasa)}
          </span>
          {esito && (
            <Badge className={cn('px-1.5', COLORE_ESITO[esito])} title={ETICHETTA_ESITO[esito]}>
              {LETTERA_ESITO[esito]}
            </Badge>
          )}
        </span>
      )}
    </Link>
  );
}
